import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';

const MODE_COLORS = {
  REFLECT: 'text-violet-400 border-violet-500/30 bg-violet-500/5',
  CONSOLIDATE: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/5',
  CURIOSITY: 'text-amber-400 border-amber-500/30 bg-amber-500/5',
  DISCOVER: 'text-sky-400 border-sky-500/30 bg-sky-500/5',
  UNKNOWN: 'text-muted-foreground border-border bg-muted/5',
};

function formatTimestamp(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function ThoughtCard({ thought, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  const mode = thought.mode || 'UNKNOWN';
  const modeColor = MODE_COLORS[mode] || MODE_COLORS.UNKNOWN;

  const thoughtText = thought.thought || '';
  const isLong = thoughtText.length > 200;
  const displayText = expanded || !isLong ? thoughtText : thoughtText.slice(0, 200) + '...';

  return (
    <Card className="transition-all hover:shadow-sm">
      <CardContent className={cn('space-y-2', compact ? 'p-3' : 'p-4')}>
        {/* Header: mode + time + cycle */}
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={cn('text-[10px] font-mono', modeColor)}>
            {mode}
          </Badge>
          <span className="text-xs text-muted-foreground">
            cycle {thought.cycle_number}
          </span>
          <span className="text-xs text-muted-foreground ml-auto">
            {formatTimestamp(thought.created_at)}
          </span>
        </div>

        {/* Thought text */}
        <p className="text-sm leading-relaxed text-foreground/80 whitespace-pre-wrap">
          {displayText}
        </p>

        {isLong && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? 'Show less' : 'Read more'}
          </button>
        )}

        {/* Stats row */}
        {!compact && (
          <div className="flex gap-3 text-[11px] text-muted-foreground pt-1">
            <span>{thought.events_processed} events</span>
            <span>{thought.insights_created} insights</span>
            <span>{thought.patterns_found} patterns</span>
            <span>{thought.connections_confirmed} confirmed</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
